import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Watchdog for QR art generation in the orders.re3gifts.com tool. Orders land
 * there with their codes PEND and a background job renders the QR SVGs
 * (~30s); when that job fails the orders sit in codes_creating and nothing
 * here can process them. The tool's own repair renders 10 codes per click —
 * this banner loops it, one batch per request, until nothing is left.
 */
const BATCH_SECONDS = 30; // observed: ~3s per code on the tool side, 10 per batch

// "Dismiss" hides the banner for the current set of stuck orders only: the
// stored signature is the stuck list, so a new or changed stuck order brings
// the banner back on the next load. Needed because an order whose only PEND
// code has a '#' in the serial (MSE4615) stays "stuck" until someone renames
// the serial in the orders tool — weeks, not minutes.
const DISMISS_KEY = 'qrPendingBanner.dismissed';

function stuckSignature(stuck) {
  return stuck
    .map((o) => `${o.order_uid}:${o.pend}`)
    .sort()
    .join(',');
}

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) || '';
  } catch {
    return '';
  }
}

function writeDismissed(sig) {
  try {
    window.localStorage.setItem(DISMISS_KEY, sig);
  } catch {
    // private mode / storage blocked — the dismiss just won't survive a reload
  }
}

function ageLabel(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

function etaLabel(remaining) {
  const secs = Math.ceil(remaining / 10) * BATCH_SECONDS;
  if (secs < 90) return '~1 min';
  return `~${Math.round(secs / 60)} min`;
}

export default function QrPendingBanner({ secretKey, onNeedAuth, onAuthFailed, onFixed }) {
  const [status, setStatus] = useState(null);
  // { startPending, fixed, remaining, running, stopped, error, toolErrors }
  const [run, setRun] = useState(null);
  const [dismissedSig, setDismissedSig] = useState(readDismissed);
  const stopRef = useRef(false);
  const runningRef = useRef(false);

  const load = useCallback(() => {
    return fetch('/api/qr/pending')
      .then((r) => r.json())
      .then((d) => setStatus(d && !d.error ? d : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFix = async () => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    if (runningRef.current) return;
    runningRef.current = true;
    stopRef.current = false;

    const startPending = status?.numPending ?? status?.stuckCodes ?? 0;
    let fixed = 0;
    setRun({
      startPending,
      fixed,
      remaining: startPending,
      running: true,
      waiting: false,
      stopped: false,
      error: null,
      unrenderableOnly: false,
      toolErrors: [],
    });

    try {
      // Loop until the tool reports nothing pending, the operator stops it,
      // or a batch makes no progress (otherwise a wedged tool loops forever)
      let more = true;
      while (more) {
        const res = await fetch('/api/qr/fix', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ secret: secretKey }),
        });
        const d = await res.json();
        if (res.status === 403) {
          onAuthFailed();
          return;
        }
        if (!res.ok) throw new Error(d.error || 'Fix request failed');

        // The tool is mid-way through creating an order — repairing codes now
        // can break that insert (MSE5142, Sep 9 2026). Wait and try again.
        if (d.busy) {
          setRun((r) => ({ ...r, waiting: true, remaining: d.numPending }));
          await new Promise((resolve) => setTimeout(resolve, 20000));
          if (stopRef.current) more = false;
          continue;
        }

        // Block-scoped copies so the state updater never closes over the
        // loop-mutated counter (react-app's no-loop-func rule)
        const nextFixed = fixed + d.numProcessed;
        const remaining = d.numPending;
        const toolErrors = d.errors || [];
        fixed = nextFixed;
        setRun((r) => ({
          ...r,
          fixed: nextFixed,
          remaining,
          waiting: false,
          toolErrors: [...r.toolErrors, ...toolErrors].slice(-5),
        }));

        if (d.numPending <= 0) {
          more = false;
        } else if (d.unrenderableOnly) {
          // Only codes the tool can never render are left (a '#' in the
          // serial) — running more batches would just fail them again
          setRun((r) => ({ ...r, unrenderableOnly: true }));
          more = false;
        } else if (d.numProcessed === 0) {
          throw new Error(
            'The tool processed nothing on the last batch — stopped so this does not loop forever. ' +
              'Check orders.re3gifts.com directly.'
          );
        } else if (stopRef.current) {
          more = false;
        }
      }
      setRun((r) => ({ ...r, stopped: stopRef.current }));
    } catch (err) {
      setRun((r) => ({ ...r, error: err.message }));
    } finally {
      runningRef.current = false;
      setRun((r) => (r ? { ...r, running: false } : r));
      await load();
      if (fixed > 0 && onFixed) onFixed();
    }
  };

  if (!status) return null;
  const stuck = status.stuckOrders || [];
  if (stuck.length === 0 && !run) return null;

  const sig = stuckSignature(stuck);
  if (!run && sig === dismissedSig) return null;

  const dismiss = () => {
    writeDismissed(sig);
    setDismissedSig(sig);
    setRun(null);
  };

  const oldest = stuck.length ? stuck[0] : null;
  const shown = stuck.slice(0, 14);
  const finished = run && !run.running && !run.error && (run.remaining <= 0 || run.unrenderableOnly);
  const unrenderable = status.unrenderable || 0;
  // Every stuck order is waiting only on '#' serials: the fix can't help, and
  // the banner is a reminder rather than a call to action
  const allUnrenderable = stuck.length > 0 && stuck.every((o) => (o.unrenderable_pend || 0) >= o.pend);

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-3.5 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2.5">
          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 13.5h3v3h-3zM18 18h3v3h-3zM13.5 18h1.5v1.5h-1.5zM18 13.5h1.5v1.5H18z" />
            </svg>
          </div>
          <div>
            {stuck.length > 0 ? (
              <p className="text-sm text-amber-900 font-medium">
                {allUnrenderable
                  ? `QR art for ${stuck.length} order${stuck.length !== 1 ? 's' : ''} is waiting on a serial rename in the orders tool`
                  : `QR art generation is stuck in the orders tool — ${stuck.length} order${stuck.length !== 1 ? 's' : ''} (${status.stuckCodes} code${status.stuckCodes !== 1 ? 's' : ''}) waiting`}{' '}
                {oldest && `up to ${ageLabel(oldest.oldest_pend)}`}
                {status.numPending !== null && status.numPending !== status.stuckCodes && (
                  <span className="text-amber-700 font-normal"> · tool reports {status.numPending} pending</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-amber-900 font-medium">QR backlog cleared — no orders waiting on art</p>
            )}
            {stuck.length > 0 && (
              <p className="text-xs font-mono text-amber-800 mt-1">
                {shown
                  .map((o) => `${o.order_uid} (${o.pend}/${o.codes}${o.unrenderable_pend ? ` · ${o.unrenderable_pend} # serial` : ''})`)
                  .join(' · ')}
                {stuck.length > shown.length && ` · +${stuck.length - shown.length} more`}
              </p>
            )}
            <p className="text-[11px] text-amber-700 mt-1">
              {allUnrenderable ? (
                <>
                  Nothing here can fix this: the tool's renderer rejects serials containing{' '}
                  <span className="font-mono">#</span>, so these orders stay in codes_creating until the serial is renamed
                  in the orders tool. Dismiss hides this until a different order gets stuck.
                </>
              ) : (
                <>
                  These orders can't be greenlit or batched until their codes are DONE. The fix renders 10 codes per batch
                  (~{BATCH_SECONDS}s each) — keep this tab open. It clears the backlog only; the auto-generation fault lives in
                  the orders tool itself (Render service <span className="font-mono">qr-prod</span>).
                </>
              )}
              {unrenderable > 0 && !allUnrenderable && (
                <span className="block mt-0.5">
                  {unrenderable} pending code{unrenderable !== 1 ? 's' : ''} can't be rendered (a <span className="font-mono">#</span> in the
                  serial) — the fix skips {unrenderable !== 1 ? 'them' : 'it'}; rename the serial in the orders tool to clear it.
                </span>
              )}
              {status.toolError && <span className="block text-red-700 mt-0.5">Tool unreachable: {status.toolError}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {run?.running ? (
            <>
              <span className="text-xs text-amber-900 font-medium tabular-nums">
                {run.waiting
                  ? `Paused — an order is landing in the tool · fixed ${run.fixed} · ${run.remaining} left`
                  : `Fixed ${run.fixed} · ${run.remaining} left · ${etaLabel(run.remaining)}`}
              </span>
              <button
                onClick={() => {
                  stopRef.current = true;
                }}
                disabled={stopRef.current}
                className="px-3 py-1 bg-white border border-amber-300 text-amber-800 text-xs font-semibold rounded-lg hover:bg-amber-100 disabled:opacity-40 transition-colors"
              >
                {stopRef.current ? 'Stopping after this batch…' : 'Stop'}
              </button>
            </>
          ) : (
            <>
              {run && (
                <span className="text-xs text-amber-900 tabular-nums">
                  {finished
                    ? `Done — fixed ${run.fixed}${run.unrenderableOnly ? ` · ${run.remaining} unrenderable left` : ''}`
                    : `Fixed ${run.fixed} · ${run.remaining} left`}
                </span>
              )}
              {!allUnrenderable && (
                <button
                  onClick={handleFix}
                  disabled={stuck.length === 0 && !(status.numPending > 0)}
                  className="px-3 py-1 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-40 transition-colors"
                >
                  {run ? 'Fix remaining' : 'Fix QR codes now'}
                </button>
              )}
              <button
                onClick={load}
                className="px-3 py-1 bg-white border border-amber-300 text-amber-800 text-xs font-semibold rounded-lg hover:bg-amber-100 transition-colors"
              >
                Re-check
              </button>
              {stuck.length > 0 && (
                <button
                  onClick={dismiss}
                  title="Hide this banner until a different order gets stuck"
                  className={
                    allUnrenderable
                      ? 'px-3 py-1 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors'
                      : 'px-3 py-1 bg-white border border-amber-300 text-amber-800 text-xs font-semibold rounded-lg hover:bg-amber-100 transition-colors'
                  }
                >
                  Dismiss
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {run?.running && (
        <div className="mt-2.5 ml-9 h-1.5 bg-amber-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-500 transition-all"
            style={{ width: `${run.startPending ? Math.min(100, (run.fixed / run.startPending) * 100) : 0}%` }}
          />
        </div>
      )}
      {run?.error && (
        <p className="text-[11px] text-red-800 bg-red-100 border border-red-200 rounded px-2 py-1 mt-2 ml-9 inline-block">
          {run.error}
        </p>
      )}
      {run?.toolErrors?.length > 0 && (
        <p className="text-[11px] text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1 mt-2 ml-9 inline-block">
          Tool reported: {run.toolErrors.join('; ')}
        </p>
      )}
    </div>
  );
}
